import * as React from 'react';

import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store, injectReducer } from './redux';
import { createIntersectionObserver } from '@shopee-user/intersection-observer';
import { render } from 'react-dom';

// Components
import App from './App';

// Utils
import { fetchTranslation, I18nConfig } from '@shopee-user/i18n';
import { parseQueryStringToObjInternal } from '@shopee-user/authentication-utils';
import { getUserLanguage, setUserLanguage } from './utils/language';
import { createBITracker } from '@shopee-user/tracking';
import { getCookies } from '@shopee/cookie';
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { getDeviceFingerprint } from '@shopee-user/device-fingerprint';
import registerServiceWorker from './sw/register-sw';
import {
  initSdk as initWebBridgeSdk,
  isWebBridgeSupported,
} from '@shopee/web-bridge-sdk';
import { initMonitoring } from './platform/monitoring';
import { Gtm } from './platform';

// Constants
import { TRANSIFY_COLLECTION_MAPPING } from './config/transify';
import { DEFAULT_COLLECTION } from '@shopee-user/transify-collections';
import { INTERSECTION_PERCENTAGE } from '@shopee-user/tracking';
import {
  TRACKING_APP_ID,
  TRACKING_VALIDATION_URL,
  TRACKING_VALIDATION_API_TOKEN,
} from './config/tracking';
import { Env } from '@shopee-user/global';
import { Feature } from '@shopee-user/feature-toggle';
import firebaseConfig from './config/firebase';

// Context
import { GlobalContext } from '@shopee-user/global-context';
import { InjectReducerContext } from '@shopee/with-inject-reducer';
import {
  FirebasePerformanceContext,
  createFirebasePerformanceTracker,
} from '@shopee-user-common/firebase-performance-utils';
import { TrackingContext } from '@shopee-user/tracking';
// Web API polyfill
import polyfillWebAPI from './utils/polyfill/webAPI';

// Monitoring
import { platformTracker } from './platform/monitoring/performance/tracker';
// ABTest
import { initABTest } from './platform/abtest/client';

const dest = document.getElementById('app');

const {
  initTracker: initFirebasePerformanceTracker,
  recordPerformance,
} = createFirebasePerformanceTracker({ config: firebaseConfig });

function renderApp(i18nConfig: I18nConfig) {
  const lazyLoadObserver = createIntersectionObserver({
    rootMargin: '0px 0px 50px 0px',
  });

  const trackingObserver = createIntersectionObserver({
    rootMargin: '0px 0px 0px 0px',
    threshold: INTERSECTION_PERCENTAGE,
  });

  const {
    initTracker: initBITracker,
    sendTrackingEvent,
    sendImpressionEvent,
  } = createBITracker({
    appID: TRACKING_APP_ID,
    webConfig: {
      cookies: getCookies(),
      userAgent: self.navigator.userAgent,
      validationUrl: TRACKING_VALIDATION_URL,
      validationApiToken: TRACKING_VALIDATION_API_TOKEN,
    },
    enableWebTracking: isFeatureEnabled(Feature.WebTracking),
    enableAppTracking: isFeatureEnabled(Feature.AppTracking),
    enableSchemaValidation: __ENV__ !== Env.Live,
    enableDebugTraceLogs: __ENV__ !== Env.Live && __ENABLE_TRACKING_DEBUG__,
  });

  render(
    <React.Fragment>
      <Provider store={store}>
        <BrowserRouter>
          <GlobalContext.Provider
            value={{
              userAgent: self.navigator.userAgent,
              theme: {},
              lazyLoadObserver,
            }}
          >
            <TrackingContext.Provider
              value={{
                initTracker: initBITracker,
                sendTrackingEvent,
                sendImpressionEvent,
                trackingObserver,
              }}
            >
              <FirebasePerformanceContext.Provider
                value={{
                  initTracker: initFirebasePerformanceTracker,
                  recordPerformance,
                }}
              >
                <InjectReducerContext.Provider
                  value={{
                    injectAsyncReducer: injectReducer,
                  }}
                >
                  <App i18nConfig={i18nConfig} />
                </InjectReducerContext.Provider>
              </FirebasePerformanceContext.Provider>
            </TrackingContext.Provider>
          </GlobalContext.Provider>
        </BrowserRouter>
      </Provider>
    </React.Fragment>,
    dest
  );
}

async function init() {
  const env = __ENV__ as Env;

  platformTracker.trackAppInit();

  // init FE monitoring
  initMonitoring();

  // init abtest
  // no need to await, when an AB Test fetching is called, it will wait for the sdk to be loaded
  // so here it is more like a eager loading
  initABTest();

  // Initialize web bridge SDK as early as possible to solve the WebViewJavascriptBridge not found issue on iOS
  initWebBridgeSdk();

  // please ensure `registerServiceWorker` is called before any `await`
  // Only the code before `await`s is executed synchronously. https://javascript.info/event-loop
  // Why we need `registerServiceWorker` be executed synchronously? please refer to this bug:
  // https://git.garena.com/shopee/mall-fe/core-fe-aq/account-business-web/-/merge_requests/1513
  __ENABLE_SW__ && registerServiceWorker();

  // polyfill web API on demand
  await polyfillWebAPI();

  // Generate DF if it's not there yet
  getDeviceFingerprint();

  // check language from query string
  const { lang } = parseQueryStringToObjInternal(location.search);

  let queryLang;

  if (!isWebBridgeSupported()) {
    /**
     * Do not set language from query param in cookie when web bridge is being used
     * App will set the language directly in the webview cookie
     * We should not set our own language cookie to prevent conflict with App's language
     */
    queryLang = setUserLanguage(lang);
  }

  const language = queryLang || getUserLanguage();

  // set html's lang attribute for accessibility and SEO.
  window.document.documentElement.setAttribute('lang', language);

  // wait for default collection to be loaded
  const translations = await fetchTranslation({
    language,
    env,
    collectionId: DEFAULT_COLLECTION,
    transifyMapping: TRANSIFY_COLLECTION_MAPPING,
  });

  const i18nConfig: I18nConfig = {
    initialLanguage: language,
    env,
    transifyCollectionMapping: TRANSIFY_COLLECTION_MAPPING,
    initialTranslations: translations,
    initialCollections: [DEFAULT_COLLECTION],
  };

  // Initialize GTM
  Gtm.init();

  // this will render the actual UI
  // note that we put the shell UI in the container, so after this function, the shell will disappear
  renderApp(i18nConfig);
}

void init();
